// src/utils/prescriptionStatus.js

const STATUS_CODES = {
  // Success codes
  '000': {
    status: 'pending',
    message: 'Prescription received successfully. Awaiting verification.',
    isRetryable: false,
    severity: 'info'
  },
  '010': {
    status: 'success',
    message: 'Prescription verified and processed successfully.',
    isRetryable: false,
    severity: 'success'
  },

  // Syntax and validation errors
  '900': {
    status: 'error',
    message: 'Invalid prescription format. Please check all fields.',
    isRetryable: false,
    severity: 'error'
  },
  '2000': {
    status: 'error',
    message: 'Prescription validation failed. Please review the details.',
    isRetryable: false,
    severity: 'error'
  },
  '220': {
    status: 'error',
    message: 'This prescription has already been submitted.',
    isRetryable: false,
    severity: 'warning'
  },

  // Communication and timeout errors
  '600': {
    status: 'error',
    message: 'Connection timeout. Please try again.',
    isRetryable: true,
    severity: 'warning'
  },
  '601': {
    status: 'error',
    message: 'Unable to reach pharmacy. Please try again later.',
    isRetryable: true,
    severity: 'warning'
  },
  '602': {
    status: 'error',
    message: 'Pharmacy system unable to process request.',
    isRetryable: true,
    severity: 'warning'
  },

  // Service and authorization errors
  '700': {
    status: 'error',
    message: 'Service authorization error. Please contact support.',
    isRetryable: false,
    severity: 'error'
  }
};

const DESCRIPTION_CODE_MESSAGES = {
  '3000': 'Missing or invalid information in prescription.',
  '4000': 'Unable to deliver to recipient.',
  '4020': 'System processing error.',
  '4030': 'Not authorized to send prescriptions.',
  '4040': 'Pharmacy does not support electronic prescriptions.',
  '008': 'Request timed out.',
  '210': 'Communication problem with pharmacy system.'
};

export function getPrescriptionStatus(response) {
  try {
    // Handle null or undefined response
    if (!response?.Status) {
      return {
        status: 'error',
        message: 'Unable to process prescription response.',
        isRetryable: true,
        severity: 'error',
        details: null
      };
    }

    const { Code, Description, DescriptionCode } = response.Status;
    const statusInfo = STATUS_CODES[Code] || {
      status: 'error',
      message: 'Unknown response code.',
      isRetryable: false,
      severity: 'error'
    };

    // Enrich the message with more specific description if available
    const descriptionMessage = DESCRIPTION_CODE_MESSAGES[DescriptionCode];
    const enrichedMessage = descriptionMessage 
      ? `${statusInfo.message} ${descriptionMessage}`
      : Description || statusInfo.message;

    return {
      ...statusInfo,
      message: enrichedMessage,
      details: {
        code: Code,
        descriptionCode: DescriptionCode,
        originalDescription: Description,
        responseCode: response.ResponseCode
      }
    };
  } catch (error) {
    console.error('Error parsing prescription status:', error);
    return {
      status: 'error',
      message: 'Error processing prescription status.',
      isRetryable: true,
      severity: 'error',
      details: error.message
    };
  }
}

// Helper to determine if a prescription needs manual intervention
export function needsManualIntervention(statusInfo) {
  return (
    statusInfo.status === 'error' &&
    !statusInfo.isRetryable &&
    statusInfo.severity === 'error'
  );
}

// Helper to determine if a prescription can be retried
export function canRetryPrescription(statusInfo) {
  return statusInfo.status === 'error' && statusInfo.isRetryable;
}